import * as Yup from "yup";

const toCurrencyCents = value =>
  Math.round((Number(value) + Number.EPSILON) * 100);

/**
 * Pure: builds the per-row validation for a single dynamic credit allocation
 * field (e.g. `amount_1`). A row's credit amount is only validated once its
 * transaction (`id_N`) has been chosen, and is bounded by the remaining net
 * amount of that line (`remainingAmount_N`).
 */
const createDynamicValidation = fieldName => {
  const fieldNumber = fieldName.match(/\d+/); // Extract the number from the field name

  return Yup.object().shape({
    ...(/^id_\d+$/.test(fieldName) && {
      [fieldName]: Yup.number().nullable().required("Select a Reference first"),
    }),
    ...(/^amount_\d+$/.test(fieldName) && {
      [fieldName]: Yup.number().when([`id_${fieldNumber}`], {
        is: id => id !== undefined && id !== null,
        then: () =>
          Yup.number()
            .moreThan(0, "Credit amount must be greater than 0")
            .test(
              "is-validAmt",
              "Cannot credit more than the remaining amount for this line",
              function (value, context) {
                const remaining =
                  context.parent[`remainingAmount_${fieldNumber}`];
                // Until the remaining amount has loaded we can't bound the row,
                // so don't block on it here.
                if (remaining === undefined || remaining === null) {
                  return true;
                }
                return toCurrencyCents(value) <= toCurrencyCents(remaining);
              },
            )
            .required("Please enter a credit amount")
            .typeError("Enter a valid amount"),
        otherwise: () => Yup.number().notRequired(),
      }),
    }),
  });
};

/**
 * Pure: assembles the full credit-note validation schema for the current form
 * values. Requires at least one Credit line and adds one dynamic rule per
 * allocation row, so the schema grows as rows are added.
 */
const buildCreditNoteValidationSchema = formData => {
  const validationSchema = Yup.object({
    items: Yup.array()
      .min(1, "Add at least one Credit line")
      .required("Add at least one Credit line"),
  });

  if (!formData || typeof formData !== "object") {
    return validationSchema;
  }

  return Object.keys(formData).reduce(
    (schema, fieldName) => schema.concat(createDynamicValidation(fieldName)),
    validationSchema,
  );
};

export { buildCreditNoteValidationSchema, createDynamicValidation };
